import React from 'react';
import Box from '@amzn/awsui-components-console/box';
import Modal from '@amzn/awsui-components-console/modal';
import Table from '@amzn/awsui-components-console/table';
import { NOT_AVAILABLE } from '../constants';
import { FieldDiff } from '../utils/versionFieldDiff';

interface StringVersionDiffModalProps {
  visible: boolean;
  stringId: string;
  /** The version whose change is shown — the Current column. */
  version: number;
  /** Its predecessor in the version list — the Previous column. */
  previousVersion: number;
  sourceText: FieldDiff;
  contextNote: FieldDiff;
  onDismiss: () => void;
}

/** One field's row in the diff table. */
interface FieldRow {
  field: string;
  diff: FieldDiff;
}

/**
 * What one version changed in a single string, field by field.
 *
 * Presentational only: both sides of the comparison come from snapshots the
 * panel already holds, so opening this modal fetches nothing. The group page's
 * `DiffModal` is not reused because it diffs a whole group's string set and
 * fetches two group versions to do it.
 */
const StringVersionDiffModal: React.FC<StringVersionDiffModalProps> = ({
  visible,
  stringId,
  version,
  previousVersion,
  sourceText,
  contextNote,
  onDismiss,
}) => {
  const rows: FieldRow[] = [
    { field: 'Source text', diff: sourceText },
    { field: 'Context note', diff: contextNote },
  ];

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      data-testid="string-diff-modal"
      closeAriaLabel="Close version diff"
      header={`Diff: ${stringId}`}
    >
      <Table
        columnDefinitions={[
          {
            id: 'field',
            header: 'Field',
            cell: (item: FieldRow) => <Box fontWeight="bold">{item.field}</Box>,
            width: 160,
          },
          {
            // Versions are named in the headers so there is no doubt which
            // column is which version.
            id: 'previous',
            header: `Previous (v${previousVersion})`,
            cell: (item: FieldRow) => (
              <span data-testid="string-diff-previous">
                {item.diff.previous || NOT_AVAILABLE}
              </span>
            ),
          },
          {
            id: 'current',
            header: `Current (v${version})`,
            cell: (item: FieldRow) => (
              <span data-testid="string-diff-current">
                {item.diff.value || NOT_AVAILABLE}
              </span>
            ),
          },
        ]}
        items={rows}
        stripedRows
        variant="embedded"
      />
    </Modal>
  );
};

export default StringVersionDiffModal;
